import { useEffect, useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { useCapabilities } from "@components/catalog/useCapabilities";
import { CAPABILITIES } from "@api/capabilities";
import { useAppSelector } from "@app/hooks";
import { resolvePaperSize } from "@lib/paperSize";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { fmtNum } from "@lib/format";
import { useMoney } from "@units/useMoney";
import {
  useGetArchMetaQuery,
  useUpdateArchMetaMutation,
  useGetReportActivitiesQuery,
  useGetCostPivotQuery,
} from "@api/pApi";
import { downloadPFile } from "@api/pDownload";
import { ARCH_TEST_READING_GROUPS, ARCH_TEST_READING_LABEL } from "./archTestReadings";
import type { ArchMeta, DailyReportOut, EventOut } from "@api/types";
import "./arch.css";

/** One editable test-reading row (key + value), string-typed for the inputs. */
interface TestRow {
  key: string;
  value: string;
}

/** The empty/default meta used before the server GET resolves (or when none). */
function emptyMeta(objetivoDefault: string | null): ArchMeta {
  return {
    objetivo: objetivoDefault,
    yacimiento: null,
    tipo_trabajo: null,
    con_sin_torre: null,
    operator_nombre: null,
    operator_cc: null,
    observaciones: { s4_3: null, s5_3: null, s6_1: null, s6_2: null, s7: null },
    test_readings: {},
  };
}

/** Server meta → the editable form shape (never crashes on a partial body). */
function toForm(meta: ArchMeta | undefined, objetivoDefault: string | null) {
  const m = meta ?? emptyMeta(objetivoDefault);
  const obs = m.observaciones ?? emptyMeta(null).observaciones;
  const readings = m.test_readings ?? {};
  const tests: TestRow[] = Object.entries(readings).map(([key, value]) => ({
    key,
    value: value == null ? "" : String(value),
  }));
  return {
    objetivo: m.objetivo ?? objetivoDefault ?? "",
    yacimiento: m.yacimiento ?? "",
    tipo_trabajo: m.tipo_trabajo ?? "",
    con_sin_torre: m.con_sin_torre ?? "",
    operator_nombre: m.operator_nombre ?? "",
    operator_cc: m.operator_cc ?? "",
    s4_3: obs.s4_3 ?? "",
    s5_3: obs.s5_3 ?? "",
    s6_1: obs.s6_1 ?? "",
    s6_2: obs.s6_2 ?? "",
    s7: obs.s7 ?? "",
    tests,
  };
}

type Form = ReturnType<typeof toForm>;

/** The editable form → the ArchMeta PUT body (empty strings → null). */
function toMeta(f: Form): ArchMeta {
  const n = (s: string) => (s.trim() ? s : null);
  const test_readings: ArchMeta["test_readings"] = {};
  for (const row of f.tests) {
    const k = row.key.trim();
    if (k) test_readings[k] = row.value.trim() ? row.value : null;
  }
  return {
    objetivo: n(f.objetivo),
    yacimiento: n(f.yacimiento),
    tipo_trabajo: n(f.tipo_trabajo),
    con_sin_torre: n(f.con_sin_torre),
    operator_nombre: n(f.operator_nombre),
    operator_cc: n(f.operator_cc),
    observaciones: {
      s4_3: n(f.s4_3),
      s5_3: n(f.s5_3),
      s6_1: n(f.s6_1),
      s6_2: n(f.s6_2),
      s7: n(f.s7),
    },
    test_readings,
  };
}

/**
 * Reporte ARCH — the regulatory panel on a workover daily report. The engineer
 * fills the MANUAL fields (objetivo, yacimiento, tipo trabajo, con/sin torre,
 * operador + CC, §4-7 observaciones + test readings) bound to GET/PUT arch-meta;
 * the DERIVED numbers (daily cost, totales, días en locación, TOTAL horas) are
 * shown read-only — computed from the day's own activities + the server KPIs,
 * never re-derived here. Two buttons stream the finished xlsx straight from
 * Oxbo-P: the ARCH Reporte Diario (this daily) and the AFE Seguimiento (event).
 */
export function ArchReportPanel({
  report,
  event,
  readOnly = false,
}: {
  report: DailyReportOut;
  event: EventOut | undefined;
  readOnly?: boolean;
}) {
  const { t, locale } = useLocale();
  const { money } = useMoney();
  // Report generation is gated on report:generate (§ACL). The four generate
  // buttons only render for a holder; the server is the real gate regardless.
  const { can } = useCapabilities();
  const canGenerate = can(CAPABILITIES.reportGenerate);

  const objetivoDefault = event?.objective_1 ?? null;

  const [open, setOpen] = useState(false);

  const { data: meta } = useGetArchMetaQuery(report.id, { skip: !report.id });
  const [updateMeta, { isLoading: saving }] = useUpdateArchMetaMutation();

  // The day's time log — used ONLY to total the entered hours (warn if ≠ 24).
  const { data: activities = [] } = useGetReportActivitiesQuery(report.id, { skip: !report.id });
  // Event cost total ("totales") — server-computed; the FE only formats it.
  const { data: pivot } = useGetCostPivotQuery(
    { eventId: report.event_id },
    { skip: !report.event_id },
  );

  const [form, setForm] = useState<Form>(() => toForm(meta, objetivoDefault));
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [dlError, setDlError] = useState<string | null>(null);
  const paperPref = useAppSelector((st) => st.prefs.paperSize);
  const tenant = useAppSelector((st) => st.prefs.tenant);
  const paper = resolvePaperSize(paperPref, tenant);
  const [generating, setGenerating] = useState<string | null>(null);

  // Re-seed the form when the server meta (or the selected report) changes.
  useEffect(() => {
    setForm(toForm(meta, objetivoDefault));
    setSaved(false);
    setSaveError(false);
  }, [meta, objetivoDefault, report.id]);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };

  // TOTAL hours = sum of the day's entered activity durations (formatting-only).
  const totalHours = useMemo(
    () => activities.reduce((sum, a) => sum + (a.duration_hours ?? 0), 0),
    [activities],
  );
  const hoursOff = activities.length > 0 && Math.abs(totalHours - 24) > 0.001;
  const reportReady = activities.length > 0 && Math.abs(totalHours - 24) <= 0.001;

  const ccy =
    pivot?.currency && pivot.currency !== "MIXED" ? pivot.currency : (report.currency ?? null);

  const derived: Stat[] = [
    {
      label: t("arch.derived.dailyCost"),
      value: money(report.daily_cost, report.currency, { asOf: report.date_report }),
      tone: "ops",
    },
    {
      label: t("arch.derived.totales"),
      value: money(pivot?.total ?? null, ccy, { compact: true }),
      tone: "ops",
    },
    { label: t("arch.derived.dias"), value: fmtNum(report.days_op, locale) },
    {
      label: t("arch.derived.totalHoras"),
      value: `${fmtNum(totalHours, locale, { maximumFractionDigits: 2 })} h`,
      tone: hoursOff ? "warn" : "good",
    },
  ];

  const save = async () => {
    setSaveError(false);
    const res = await updateMeta({ dailyReportId: report.id, body: toMeta(form) })
      .unwrap()
      .catch(() => null);
    if (res) {
      setSaved(true);
    } else {
      setSaveError(true);
    }
  };

  const generate = async (kind: "report" | "afe" | "propio", fmt: "xlsx" | "pdf") => {
    setDlError(null);
    setGenerating(`${kind}-${fmt}`);
    try {
      if (kind === "report") {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Guayaquil";
        await downloadPFile(
          `/daily-reports/${report.id}/arch-report.${fmt}?tz=${encodeURIComponent(tz)}&paper=${encodeURIComponent(paper)}`,
          `ARCH_${report.well_uwi}_Nro-${report.report_no}.${fmt}`,
        );
      } else if (kind === "propio") {
        if (!event) return;
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Guayaquil";
        await downloadPFile(
          `/events/${event.id}/reporte-propio.${fmt}?tz=${encodeURIComponent(tz)}`,
          `Reporte_Propio_${event.well_uwi}.${fmt}`,
        );
      } else {
        if (!event) return;
        await downloadPFile(
          `/events/${event.id}/afe-seguimiento.${fmt}?paper=${encodeURIComponent(paper)}`,
          `AFE_Seguimiento_${event.well_uwi}.${fmt}`,
        );
      }
    } catch (e) {
      setDlError(e instanceof Error && e.message ? e.message : t("arch.downloadError"));
    } finally {
      setGenerating(null);
    }
  };

  return (
    <section className="arch-panel card">
      <button
        type="button"
        className="arch-head"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="arch-head-title">
          {t("arch.title")}
          <span className="tag-op">ARCH</span>
        </span>
        <span className="arch-chevron">{open ? "▾" : "▸"}</span>
      </button>

      {open ? (
        <div className="arch-body">
          <p className="hint">{t("arch.subtitle")}</p>

          {/* ── Derived (read-only) ─────────────────────────────────────── */}
          <div className="arch-section">
            <h4>
              {t("arch.section.derived")} <span className="tag-op">{t("arch.derived.tag")}</span>
            </h4>
            <StatBand stats={derived} />
            {hoursOff ? (
              <div className="arch-warn" role="alert">
                {t("arch.warn.hours", {
                  h: fmtNum(totalHours, locale, { maximumFractionDigits: 2 }),
                })}
              </div>
            ) : null}
          </div>

          {/* ── Manual fields ───────────────────────────────────────────── */}
          <div className="arch-section">
            <h4>{t("arch.section.manual")}</h4>
            <div className="field">
              <label htmlFor="arch-objetivo">{t("arch.field.objetivo")}</label>
              <textarea
                id="arch-objetivo"
                className="text-area"
                rows={3}
                value={form.objetivo}
                onChange={(e) => set("objetivo", e.target.value)}
                readOnly={readOnly}
              />
            </div>
            <div className="grid-2">
              <div className="field">
                <label htmlFor="arch-yacimiento">{t("arch.field.yacimiento")}</label>
                <input
                  id="arch-yacimiento"
                  type="text"
                  value={form.yacimiento}
                  onChange={(e) => set("yacimiento", e.target.value)}
                  readOnly={readOnly}
                />
              </div>
              <div className="field">
                <label htmlFor="arch-tipo">{t("arch.field.tipoTrabajo")}</label>
                <select
                  id="arch-tipo"
                  value={form.tipo_trabajo}
                  onChange={(e) => set("tipo_trabajo", e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">—</option>
                  <option value="OPEX">{t("arch.opex")}</option>
                  <option value="CAPEX">{t("arch.capex")}</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="arch-torre">{t("arch.field.conSinTorre")}</label>
                <select
                  id="arch-torre"
                  value={form.con_sin_torre}
                  onChange={(e) => set("con_sin_torre", e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">—</option>
                  <option value="con torre">{t("arch.conTorre")}</option>
                  <option value="sin torre">{t("arch.sinTorre")}</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="arch-op-nombre">{t("arch.field.operatorNombre")}</label>
                <input
                  id="arch-op-nombre"
                  type="text"
                  value={form.operator_nombre}
                  onChange={(e) => set("operator_nombre", e.target.value)}
                  readOnly={readOnly}
                />
              </div>
              <div className="field">
                <label htmlFor="arch-op-cc">{t("arch.field.operatorCc")}</label>
                <input
                  id="arch-op-cc"
                  type="text"
                  value={form.operator_cc}
                  onChange={(e) => set("operator_cc", e.target.value)}
                  readOnly={readOnly}
                />
              </div>
            </div>
          </div>

          {/* ── Observaciones §4-7 (collapsible) ────────────────────────── */}
          <details className="arch-section arch-collapsible">
            <summary>{t("arch.section.observaciones")}</summary>
            {(
              [
                ["s4_3", "arch.obs.s4_3"],
                ["s5_3", "arch.obs.s5_3"],
                ["s6_1", "arch.obs.s6_1"],
                ["s6_2", "arch.obs.s6_2"],
                ["s7", "arch.obs.s7"],
              ] as const
            ).map(([key, labelKey]) => (
              <div className="field" key={key}>
                <label htmlFor={`arch-${key}`}>{t(labelKey)}</label>
                <textarea
                  id={`arch-${key}`}
                  className="text-area"
                  rows={2}
                  value={form[key]}
                  onChange={(e) => set(key, e.target.value)}
                  readOnly={readOnly}
                />
              </div>
            ))}
          </details>

          {/* ── Test readings §4-7 (collapsible, optional) ──────────────── */}
          <details className="arch-section arch-collapsible">
            <summary>{t("arch.section.tests")}</summary>
            <p className="hint">{t("arch.tests.hint")}</p>
            {form.tests.map((row, i) => (
              <div className="grid-2 arch-test-row" key={i}>
                <div className="field">
                  <label htmlFor={`arch-test-k-${i}`}>{t("arch.tests.key")}</label>
                  <select
                    id={`arch-test-k-${i}`}
                    value={row.key}
                    disabled={readOnly}
                    onChange={(e) =>
                      set(
                        "tests",
                        form.tests.map((r, j) => (j === i ? { ...r, key: e.target.value } : r)),
                      )
                    }
                  >
                    <option value="">{t("arch.tests.choose")}</option>
                    {/* A saved key not in the catalog (legacy/custom) stays selectable. */}
                    {row.key && !ARCH_TEST_READING_LABEL[row.key] ? (
                      <option value={row.key}>{row.key}</option>
                    ) : null}
                    {ARCH_TEST_READING_GROUPS.map((g) => (
                      <optgroup key={g.section} label={g.section}>
                        {g.options.map((o) => (
                          <option key={o.key} value={o.key}>
                            {o.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor={`arch-test-v-${i}`}>{t("arch.tests.value")}</label>
                  <input
                    id={`arch-test-v-${i}`}
                    type="text"
                    value={row.value}
                    onChange={(e) =>
                      set(
                        "tests",
                        form.tests.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)),
                      )
                    }
                    readOnly={readOnly}
                  />
                </div>
              </div>
            ))}
            {!readOnly ? (
              <button
                type="button"
                className="btn"
                onClick={() => set("tests", [...form.tests, { key: "", value: "" }])}
              >
                {t("arch.tests.add")}
              </button>
            ) : null}
          </details>

          {/* ── Actions ─────────────────────────────────────────────────── */}
          <div className="arch-actions">
            {!readOnly ? (
              <button type="button" className="btn primary" disabled={saving} onClick={save}>
                {saving ? t("arch.saving") : t("arch.save")}
              </button>
            ) : null}
            {saved ? <span className="badge status-producing">{t("arch.saved")}</span> : null}
            {saveError ? <span className="arch-inline-error">{t("arch.saveError")}</span> : null}

            <span className="arch-actions-spacer" />

            {canGenerate ? (
              <>
                <button
                  type="button"
                  className="btn"
                  disabled={generating !== null || !reportReady}
                  onClick={() => void generate("report", "xlsx")}
                >
                  {generating === "report-xlsx" ? t("arch.generating") : t("arch.generateReport")}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={generating !== null || !reportReady}
                  onClick={() => void generate("report", "pdf")}
                >
                  {generating === "report-pdf" ? t("arch.generating") : t("arch.reportPdf")}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={generating !== null || !event}
                  onClick={() => void generate("afe", "xlsx")}
                >
                  {generating === "afe-xlsx" ? t("arch.generating") : t("arch.generateAfe")}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={generating !== null || !event}
                  onClick={() => void generate("afe", "pdf")}
                >
                  {generating === "afe-pdf" ? t("arch.generating") : t("arch.afePdf")}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={generating !== null || !event}
                  onClick={() => void generate("propio", "xlsx")}
                >
                  {generating === "propio-xlsx" ? t("arch.generating") : t("arch.propioXlsx")}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={generating !== null || !event}
                  onClick={() => void generate("propio", "pdf")}
                >
                  {generating === "propio-pdf" ? t("arch.generating") : t("arch.propioPdf")}
                </button>
              </>
            ) : null}
          </div>
          {dlError ? <div className="arch-inline-error">{dlError}</div> : null}
        </div>
      ) : null}
    </section>
  );
}
